import { Controller } from "@hotwired/stimulus";

type ManifestType = {
  name: string;
  url: string;
  hook_attributes: {
    url: string;
  };
  redirect_url: string;
  setup_url: string;
  public: boolean;
  default_permissions: {
    [key: string]: string;
  };
  default_events: string[];
};

// Turns out GitHub is very particular about the "default_permissions" and doesn't _actually_ document
// the list of what's available anywhere. I browsed the source of our GHES settings and looked at the form elements:
// https://buildkite-ghe.com/organizations/mdc/settings/apps/buildkite-with-code-access/permissions
const DEFAULT_PERMISSIONS = {
  checks: "write",
  deployments: "write",
  issues: "write",
  metadata: "read",
  pull_requests: "read",
  repository_hooks: "write",
  statuses: "read",
};

const DEFAULT_EVENTS = ["check_run", "check_suite", "issues", "issue_comment"];

// When creating a new GitHub Enterprise Server Application, we
// generate a manifest that is used to configure the application,
// and then perform a redirect to the GitHub Enterprise Server (which
// performs an OAuth-like handshake before redirecting back to the
// application with an app ID and secrets).
export default class extends Controller<HTMLElement> {
  static targets = [
    "form",
    "url",
    "apiProxyUrl",
    "organization",
    "codeReadAccess",
    "manifest",
    "rootUrl",
    "webhookUrl",
    "setupUrl",
    "redirectUrl",
  ];

  declare formTarget: HTMLFormElement;
  declare urlTarget: HTMLInputElement;
  declare apiProxyUrlTarget: HTMLInputElement;
  declare organizationTarget: HTMLInputElement;
  declare codeReadAccessTarget: HTMLInputElement;
  declare manifestTarget: HTMLInputElement;
  declare rootUrlTarget: HTMLInputElement;
  declare webhookUrlTarget: HTMLInputElement;
  declare setupUrlTarget: HTMLInputElement;
  declare redirectUrlTarget: HTMLInputElement;

  declare handleSubmit: () => void;

  initialize() {
    this.handleSubmit = this.submit;
  }

  generateManifest() {
    let redirectUrl = this.redirectUrlTarget.value;
    redirectUrl = redirectUrl.replace(
      "GHES_URL_B64",
      btoa(this.urlTarget.value),
    );
    redirectUrl = redirectUrl.replace(
      "GHES_API_PROXY_URL_B64",
      btoa(this.apiProxyUrlTarget.value),
    );

    const permissions = { ...DEFAULT_PERMISSIONS };
    let name = "Buildkite";
    if (this.codeReadAccessTarget.checked) {
      permissions["contents"] = "read";
      name += " (with code access)";
    }

    this.manifestTarget.value = JSON.stringify({
      name, // The name of the application must be less than 34 characters
      url: this.rootUrlTarget.value,
      hook_attributes: { url: this.webhookUrlTarget.value },
      redirect_url: redirectUrl,
      setup_url: this.setupUrlTarget.value,
      public: false,
      default_permissions: permissions,
      default_events: DEFAULT_EVENTS,
    } as ManifestType);
  }

  // The form action is determined by the organization name. If the organization
  // is empty, we're creating a global application. If the organization is set,
  // we're creating an application for that organization inside of the GitHub Enterprise Server.
  mutateFormSubmitUrl() {
    const organization = this.organizationTarget.value;
    const host = this.urlTarget.value;
    this.formTarget.action =
      organization === ""
        ? `${host}/settings/apps/new`
        : `${host}/organizations/${organization}/settings/apps/new`;
  }

  submit() {
    this.generateManifest();
    this.mutateFormSubmitUrl();
    this.formTarget.requestSubmit();
  }
}
